// Outfit Studio — a real outfit change, as opposed to a Looks recolour.
//
// Looks/retexture repaints the avatar's existing geometry: a t-shirt mesh stays
// a t-shirt whatever it is painted as. This flow goes the long way round —
// reference image → image model redraws the same character in new clothes →
// the user approves the picture → Tripo image-to-3D + rigging — and produces a
// SIBLING avatar with genuinely different garment geometry.
//
// The approval step is the point of the design: the image is cheap and fast,
// the 3D build burns a full generation credit and takes minutes. Nobody should
// spend the expensive step on an outfit they have not seen.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Check, Loader, RefreshCw, Shirt, Sparkles, Wand2, X } from 'lucide-react';
import { restyleCharacterImage } from '../services/openRouterApi';
import { buildAvatarFromImage } from '../utils/buildAvatarFromImage';
import { supabase } from '../config/supabase';
import type { Avatar } from '../services/avatarService';
import './AvatarOutfitStudio.css';

// Each idea names top, bottoms and footwear, so the preview image (and the 3D
// build from it) gets a complete outfit rather than one changed garment.
const IDEAS = [
  'black leather jacket, dark jeans, silver boots',
  'crimson silk shirt, black slacks, patent shoes',
  'white tee, blue denim jeans, white sneakers',
  'gold-trimmed robe over linen trousers and sandals',
  'neon cyan hoodie, magenta joggers, chunky trainers',
  'tailored black suit, white shirt, loafers',
  'flowing red gown with gold heels',
];

type Step = 'describe' | 'preview' | 'building' | 'done';

export const AvatarOutfitStudio: React.FC<{
  avatar: Avatar;
  onCreated: (a: Avatar) => void;
  onClose: () => void;
}> = ({ avatar, onCreated, onClose }) => {
  const [step, setStep] = useState<Step>('describe');
  const [outfit, setOutfit] = useState('');
  const [hair, setHair] = useState('');
  const [makeup, setMakeup] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [pct, setPct] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);

  // Same keystroke shield as the Looks modal — the surrounding pages attach
  // global shortcuts that otherwise eat input.
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const evts = ['keydown', 'keyup', 'keypress', 'pointerdown', 'wheel'];
    evts.forEach((t) => el.addEventListener(t, stop, { capture: true }));
    return () => evts.forEach((t) => el.removeEventListener(t, stop, { capture: true }));
  }, []);

  // Identity source: the photo the character was ORIGINALLY built from. An
  // outfit rebuild has no photo of its own, so follow restyled_from back to the
  // root — restyling a restyle (or a low-res render) drifts the face further
  // each time. The thumbnail render is the last resort.
  const [referenceUrl, setReferenceUrl] = useState<string>(avatar.reference_image_url || '');
  useEffect(() => {
    if (avatar.reference_image_url) return;
    let cancelled = false;
    (async () => {
      let from: string | undefined = avatar.creation_params?.restyled_from;
      for (let hops = 0; from && hops < 6; hops++) {
        const res: { data: { reference_image_url: string | null; creation_params: any } | null } =
          await supabase.from('avatars').select('reference_image_url, creation_params').eq('id', from).maybeSingle();
        const data = res.data;
        if (!data) break;
        if (data.reference_image_url) { if (!cancelled) setReferenceUrl(data.reference_image_url); return; }
        from = (data.creation_params as any)?.restyled_from;
      }
      if (!cancelled) setReferenceUrl(avatar.thumbnail_url || '');
    })().catch(() => { if (!cancelled) setReferenceUrl(avatar.thumbnail_url || ''); });
    return () => { cancelled = true; };
  }, [avatar.id, avatar.reference_image_url, avatar.thumbnail_url, avatar.creation_params]);

  const generatePreview = async () => {
    if (!outfit.trim()) return setError('Describe the outfit first.');
    if (!referenceUrl) return setError('This avatar has no reference image to restyle from.');
    setBusy(true);
    setError('');
    try {
      const img = await restyleCharacterImage(referenceUrl, outfit, { hair, makeup });
      setImage(img.dataUrl);
      setStep('preview');
    } catch (e: any) {
      setError(e?.message || 'Could not generate the preview.');
    } finally {
      setBusy(false);
    }
  };

  const build = async () => {
    if (!image) return;
    setStep('building');
    setError('');
    try {
      const created = await buildAvatarFromImage({
        imageDataUrl: image,
        name: `${avatar.name} — ${outfit.trim().slice(0, 30)}`,
        creationParams: { restyled_from: avatar.id, outfit, hair, makeup },
        setActive: true,
        progress: { onStage: setStage, onProgress: setPct },
      });
      setStep('done');
      onCreated(created);
    } catch (e: any) {
      setError(e?.message || 'The 3D build failed.');
      setStep('preview'); // image is still good; retrying costs nothing new
    }
  };

  return createPortal(
    <div className="aos-scrim" onClick={step === 'building' ? undefined : onClose}>
      <div className="aos-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <header className="aos-head">
          <Wand2 size={16} />
          <div>
            <h3>New outfit — {avatar.name}</h3>
            <span>
              {step === 'describe' && 'Preview the outfit as an image first. The 3D build only runs once you approve it.'}
              {step === 'preview' && 'This is what will be built. Regenerate until it is right — previews are cheap.'}
              {step === 'building' && 'Building the 3D avatar. This takes a few minutes — leave this open.'}
              {step === 'done' && 'Saved to your wardrobe and equipped.'}
            </span>
          </div>
          {step !== 'building' && (
            <button className="aos-x" onClick={onClose} aria-label="Close"><X size={17} /></button>
          )}
        </header>

        {step === 'describe' && (
          <>
            <div className="aos-compose">
              <input
                autoFocus
                value={outfit}
                onChange={(e) => setOutfit(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !busy) generatePreview(); }}
                placeholder="Describe the new outfit — a tailored black suit, a flowing red gown…"
                maxLength={300}
              />
            </div>
            <div className="aos-ideas">
              {IDEAS.map((i) => (
                <button key={i} type="button" onClick={() => setOutfit(i)}>{i}</button>
              ))}
            </div>
            <div className="aos-row">
              <label className="aos-field">
                <span>Hair colour <em>optional</em></span>
                <input value={hair} onChange={(e) => setHair(e.target.value)} placeholder="keep current" maxLength={60} />
              </label>
              <label className="aos-field">
                <span>Makeup <em>optional</em></span>
                <input value={makeup} onChange={(e) => setMakeup(e.target.value)} placeholder="keep current" maxLength={80} />
              </label>
            </div>
            {error && <p className="aos-error">{error}</p>}
            <div className="aos-actions">
              <button className="aos-primary" onClick={generatePreview} disabled={busy}>
                {busy ? <Loader size={14} className="spin" /> : <Sparkles size={14} />}
                {busy ? 'Generating preview…' : 'Preview outfit'}
              </button>
            </div>
          </>
        )}

        {step === 'preview' && image && (
          <>
            <div className="aos-compare">
              {referenceUrl && (
                <figure>
                  <img src={referenceUrl} alt="Your avatar now" />
                  <figcaption>Now</figcaption>
                </figure>
              )}
              <figure>
                <img src={image} alt="Outfit preview" />
                <figcaption>New outfit</figcaption>
              </figure>
            </div>
            <p className="aos-note aos-facecheck">
              Check the face before building — skin tone, eyes, lips, makeup. The 3D
              build copies this picture, so if the face drifted, Regenerate first.
            </p>
            {error && <p className="aos-error">{error}</p>}
            <div className="aos-actions">
              <button className="aos-ghost" onClick={() => setStep('describe')}>
                <ArrowLeft size={14} /> Change description
              </button>
              <button className="aos-ghost" onClick={generatePreview} disabled={busy}>
                {busy ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />} Regenerate
              </button>
              <button className="aos-primary" onClick={build}>
                <Check size={14} /> Build 3D avatar
              </button>
            </div>
            <p className="aos-note">
              Building uses one avatar generation credit and creates a new avatar
              in your wardrobe — the original is untouched.
            </p>
          </>
        )}

        {step === 'building' && (
          <div className="aos-building">
            <Loader size={22} className="spin" />
            <strong>{stage || 'Working…'}</strong>
            <div className="aos-bar"><span style={{ width: `${pct}%` }} /></div>
            <p>Rigging and animations are the slow part. A few minutes is normal.</p>
          </div>
        )}

        {step === 'done' && (
          <div className="aos-building">
            <Shirt size={22} />
            <strong>Outfit built and equipped</strong>
            <p>The new avatar is in your wardrobe next to the original.</p>
            <button className="aos-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AvatarOutfitStudio;
